import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, GripVertical, Maximize2, Minimize2, X } from "lucide-react";

import { useModalFocus } from "../useModalFocus";
import { cn } from "../lib/cn";

interface DrawerTab {
  id: string;
  label: string;
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;

  /** Allows the drawer to be closed with Escape. */
  closeOnEscape?: boolean;
  closeDisabled?: boolean;

  /** Navigation through items, e.g. detections list. */
  canGoPrev?: boolean;
  canGoNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  itemIndex?: number;
  itemCount?: number;
  itemLabel?: string;

  /** Allows callers to hide previous / next controls when the drawer is not list-driven. */
  showNavigation?: boolean;

  /** Allows callers to hide the footer counter when it is not relevant. */
  showItemCounter?: boolean;

  /** Allows callers to hide the resize handle. */
  showResizeGrip?: boolean;

  /** Allows callers to hide the expand / reduce control. */
  showExpandControl?: boolean;

  /** Header content. */
  title: string;
  subtitle?: ReactNode;
  badges?: ReactNode;

  /** Optional 4-column meta grid rendered below the title. */
  meta?: ReactNode;

  /** Tab bar driven by the parent. */
  tabs?: DrawerTab[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;

  /** Scrollable body. */
  children: ReactNode;
  bodyLayout?: "document" | "workspace";

  /** Right-side footer slot, e.g. action buttons. */
  footer?: ReactNode;

  defaultWidthVw?: number;
}

const MIN_WIDTH_VW = 36;
const MAX_WIDTH_VW = 86;
const EXPANDED_WIDTH_VW = 86;
const MIN_WIDTH_PX = 300;
const MAX_WIDTH_PX = 1440;

function clampWidthVw(value: number) {
  return Math.max(MIN_WIDTH_VW, Math.min(MAX_WIDTH_VW, Math.round(value)));
}

function isTextEditingTarget(target: EventTarget | null) {
  const element = target as HTMLElement | null;

  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    Boolean(element?.isContentEditable)
  );
}

function RailButton({
  label,
  onClick,
  disabled = false,
  compact = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  compact?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center justify-center border border-border bg-surface-elevated/95 text-muted",
        "shadow-lg shadow-black/10 backdrop-blur-sm transition-colors",
        "hover:text-primary disabled:cursor-not-allowed disabled:opacity-30",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
        compact
          ? "h-8 w-8 rounded-full border-0 bg-transparent shadow-none"
          : "h-10 w-10 rounded-full",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Full-viewport right-side detail drawer.
 *
 * Rendered through a portal so fixed-position controls are not affected by
 * dashboard/widget wrappers, overflow clipping, or arbitrary child sizing.
 */
export function SectionDetailDrawer({
  open,
  onClose,
  closeOnEscape = true,
  closeDisabled = false,
  canGoPrev = false,
  canGoNext = false,
  onPrev,
  onNext,
  itemIndex,
  itemCount,
  itemLabel = "items",
  showNavigation = true,
  showItemCounter = true,
  showResizeGrip = true,
  showExpandControl = true,
  title,
  subtitle,
  badges,
  meta,
  tabs,
  activeTab,
  onTabChange,
  children,
  bodyLayout = "document",
  footer,
  defaultWidthVw = 36,
}: DrawerProps) {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [widthVw, setWidthVw] = useState(clampWidthVw(defaultWidthVw));
  const [expanded, setExpanded] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const effectiveWidthVw = expanded ? EXPANDED_WIDTH_VW : widthVw;
  const drawerWidth = `min(${MAX_WIDTH_PX}px, max(${MIN_WIDTH_PX}px, ${effectiveWidthVw}vw))`;

  useEffect(() => {
    setWidthVw(clampWidthVw(defaultWidthVw));
  }, [defaultWidthVw]);

  useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  useModalFocus({
    open,
    onClose,
    dialogRef,
    closeOnEscape: false,
  });

  useEffect(() => {
    if (!open || closeDisabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      const escapeHandledByChild = target?.closest("[data-escape-key-handled='true']");

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "e") {
        if (isTextEditingTarget(event.target)) return;

        event.preventDefault();
        setExpanded((current) => !current);
        return;
      }

      if (!closeOnEscape) return;
      if (event.key !== "Escape") return;
      if (isTextEditingTarget(event.target) || escapeHandledByChild) return;

      event.preventDefault();
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeOnEscape, closeDisabled, onClose]);

  function toggleExpanded() {
    setExpanded((current) => !current);
  }

  const startResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setExpanded(false);

    const handleMove = (pointerEvent: PointerEvent) => {
      const next = ((window.innerWidth - pointerEvent.clientX) / window.innerWidth) * 100;

      setWidthVw(clampWidthVw(next));
    };

    const stop = () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", stop);
    };

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", stop);
  };

  const hasNav = showNavigation && (onPrev !== undefined || onNext !== undefined);

  const shouldShowItemCounter = showItemCounter && itemCount !== undefined;
  const shouldShowFooter = footer != null;

  const drawer = (
    <>
      <div
        aria-hidden
        onClick={closeDisabled ? undefined : onClose}
        className={cn(
          "fixed inset-0 z-40 bg-overlay-drawer transition-opacity duration-200",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        )}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Closed, the drawer stays mounted for its slide animation: keep it out of the
        // accessibility tree and out of "is a dialog open?" checks such as the Shift+F shortcut.
        aria-hidden={!open}
        inert={!open}
        tabIndex={-1}
        className={cn(
          "fixed bottom-0 right-0 top-0 z-50 w-full md:w-(--drawer-width) md:max-w-[calc(100vw-3.25rem)] transition-transform duration-300 ease-out motion-reduce:transition-none",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
        style={
          {
            "--drawer-width": drawerWidth,
            transform: open ? "translateX(0)" : "translateX(calc(100% + 3rem))",
          } as CSSProperties
        }
      >
        <div className="absolute right-4 top-4 z-10 flex flex-col items-center gap-2.5 md:right-full md:pr-2">
          <RailButton label="Close panel, Escape" onClick={onClose} disabled={closeDisabled}>
            <X className="h-4 w-4" />
          </RailButton>

          {showExpandControl ? (
            <div className="hidden md:block">
              <RailButton
                label={
                  expanded
                    ? "Reduce panel width, Command or Control E"
                    : "Expand panel width, Command or Control E"
                }
                onClick={toggleExpanded}
              >
                {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </RailButton>
            </div>
          ) : null}

          {hasNav ? (
            <div className="hidden rounded-full border border-border bg-surface-elevated/95 p-0.5 shadow-lg shadow-black/10 backdrop-blur-sm md:block">
              <RailButton
                label="Previous"
                onClick={onPrev ?? (() => {})}
                disabled={!canGoPrev}
                compact
              >
                <ChevronUp className="h-4 w-4" />
              </RailButton>

              <RailButton label="Next" onClick={onNext ?? (() => {})} disabled={!canGoNext} compact>
                <ChevronDown className="h-4 w-4" />
              </RailButton>
            </div>
          ) : null}
        </div>

        {showResizeGrip ? (
          <button
            type="button"
            aria-label="Resize panel"
            onPointerDown={startResize}
            className={cn(
              "absolute left-0 top-1/2 z-10 hidden h-16 w-6 -translate-x-1/2 -translate-y-1/2 md:flex",
              "cursor-ew-resize items-center justify-center rounded-full border border-border",
              "bg-surface-elevated/95 text-muted shadow-lg shadow-black/10 backdrop-blur-sm",
              "transition-colors hover:text-primary",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
            )}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        ) : null}

        <aside
          className={cn(
            "flex h-full w-full flex-col overflow-hidden",
            "border-l border-border bg-surface",
          )}
        >
          <header className="shrink-0 border-b border-border py-5 pl-6 pr-16 md:pr-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1 basis-64">
                <h2 className="break-words text-xl font-bold leading-snug tracking-tight text-primary">
                  {title}
                </h2>

                {subtitle ? (
                  <p className="mt-1.5 break-words text-sm text-muted">{subtitle}</p>
                ) : null}
              </div>

              {badges ? (
                <div className="flex max-w-full flex-wrap items-center gap-2">{badges}</div>
              ) : null}
            </div>

            {shouldShowItemCounter ? (
              <span className="mt-3 block text-xs tabular-nums text-muted">
                {itemCount === 0 ? 0 : (itemIndex ?? 0) + 1} of {itemCount} {itemLabel}
              </span>
            ) : null}

            {meta ? <div className="mt-4 border-t border-border pt-4">{meta}</div> : null}
          </header>

          {tabs && tabs.length > 0 ? (
            <nav className="flex shrink-0 border-b border-border px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onTabChange?.(tab.id)}
                  className={cn(
                    "relative px-4 py-3 text-sm font-semibold text-muted transition-colors",
                    "hover:text-primary focus-visible:outline-none",
                    activeTab === tab.id &&
                      "text-brand-cyan after:absolute after:bottom-0 after:left-4 after:right-4 after:h-0.5 after:rounded-full after:bg-brand-cyan",
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          ) : null}

          <div
            className={cn(
              "min-h-0 flex-1",
              bodyLayout === "workspace" ? "overflow-hidden" : "overflow-y-auto px-6 py-5",
            )}
          >
            {children}
          </div>

          {shouldShowFooter ? (
            <footer
              className={cn(
                "flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-border bg-surface px-6 py-3",
              )}
            >
              {footer ? <div className="flex flex-wrap items-center gap-2">{footer}</div> : null}
            </footer>
          ) : null}
        </aside>
      </div>
    </>
  );

  if (!portalRoot) return null;

  return createPortal(drawer, portalRoot);
}
